import { useCallback, useEffect, useMemo, useState } from 'react';
import uniqBy from 'lodash/uniqBy';

import useEvent from '@lyearn/core/hooks/useEvent';
import { getHandleEventPlugin, getInputPlaceholderPlugin } from '@lyearn/lygo-blocks';
import { EditorState, EditorView } from '@lyearn/lygo-core';
import initEditor from '@/modules/corporate/modules/channel/views/ChannelDetails/hooks/useMessageInput/utils/initEditor';
import onEditorKeyDown from '@/modules/corporate/modules/channel/views/ChannelDetails/hooks/useMessageInput/utils/onEditorKeyDown';
import onMentionButtonClick from '@/modules/corporate/modules/channel/views/ChannelDetails/hooks/useMessageInput/utils/onMentionButtonClick';
import parseMessage from '@/modules/corporate/modules/channel/views/ChannelDetails/utils/parseMessage';
import { isSubmitEvent } from '@/utils/events';

import useAutocompleteSuggestions from './useAutocompleteSuggestions';

function useCoachInput({
  placeholder,
  onInputChange,
  onEnter,
  useAutocomplete,
}: {
  placeholder: string;
  onInputChange?: (editorMessage: string, textContent: string) => void;
  onEnter?: (text: string) => void;
  useAutocomplete?: boolean;
}) {
  const [textContent, setTextContent] = useState<string | null>(null);
  const [editorView, setEditorView] = useState<EditorView | undefined>();
  const { suggestions, setSuggestions, handleAutoCompleteSuggestions } =
    useAutocompleteSuggestions();

  const { plugins, schema, nodePerformanceMap } = useMemo(() => initEditor(), []);

  const handleKeyDown = (view: EditorView, event: KeyboardEvent) => {
    const res = onEditorKeyDown(view, event);
    if (isSubmitEvent(event)) {
      const text = view.dom.textContent?.trim();
      if (text?.length) {
        const { message } = parseMessage(view.dom);
        onEnter?.(message);
        view.dom.innerHTML = '';
      }
    }
    return res;
  };

  const onEditorChange = useEvent(() => {
    if (!editorView) {
      return;
    }

    setTextContent(editorView.dom.textContent);

    const { message } = parseMessage(editorView.dom);
    onInputChange?.(message, editorView.dom.textContent || '');
    if (useAutocomplete) {
      handleAutoCompleteSuggestions(editorView.dom.textContent || '');
    }
  });

  const onSuggestionClick = useEvent((suggestion: string) => {
    if (!editorView) {
      return;
    }

    const { state } = editorView;
    editorView.dispatch(
      state.tr.replaceWith(0, state.doc.content.size, state.schema.text(suggestion)),
    );
    editorView.dom.focus();
    onInputChange?.(suggestion, suggestion);
    setSuggestions([]);
  });

  useEffect(() => {
    if (!editorView) {
      return;
    }

    editorView.updateState(
      EditorState.create({
        schema,
        plugins: uniqBy(
          [
            ...getHandleEventPlugin({ onKeyDown: handleKeyDown }),
            ...getInputPlaceholderPlugin(placeholder),
            ...editorView.state.plugins,
          ],
          'key',
        ),
      }),
    );
  }, [editorView]);

  const { message } = editorView ? parseMessage(editorView.dom) : { message: '' };

  const onMentionButtonClickCallback = useCallback(
    () => onMentionButtonClick(editorView),
    [editorView],
  );

  return {
    editorProps: {
      editable: true,
      nodePerformanceMap,
      onChange: onEditorChange,
      onCreateEditorView: setEditorView,
      plugins,
      schema,
      message,
      textContent,
    },
    mentionsButtonProps: {
      onMentionButtonClick: onMentionButtonClickCallback,
    },
    autocomplete: {
      suggestions,
      setSuggestions,
      onSuggestionClick,
    },
  };
}

export default useCoachInput;
